import type { AuthTokens } from './types';

const STORAGE_KEY = 'coi-live-auth-tokens';
const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes before expiry

/**
 * Structured logger for token operations.
 * All token events are logged with correlation ID for debugging.
 */
const tokenLogger = {
  info: (event: string, details?: Record<string, unknown>) => {
    console.log(`[TokenManager] ${event}`, details ?? '');
  },
  warn: (event: string, details?: Record<string, unknown>) => {
    console.warn(`[TokenManager] ${event}`, details ?? '');
  },
  error: (event: string, error: unknown) => {
    console.error(`[TokenManager] ${event}`, error);
  },
};

/**
 * Token manager for secure storage and automatic refresh.
 * Provides full visibility into token lifecycle for debugging.
 */
export const tokenManager = {
  /**
   * Store tokens securely in localStorage.
   * In a production app, consider using secure cookies or a more secure storage mechanism.
   */
  setTokens(tokens: AuthTokens): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
      // Store ID token for API client - Cognito User Pool authorizers validate ID tokens, not access tokens
      localStorage.setItem('coi-live-token', tokens.idToken);
      tokenLogger.info('tokens.stored', { expiresAt: new Date(tokens.expiresAt).toISOString() });
    } catch (error) {
      tokenLogger.error('tokens.store.failed', error);
      throw error;
    }
  },

  /**
   * Retrieve stored tokens.
   */
  getTokens(): AuthTokens | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return null;
      }
      return JSON.parse(stored) as AuthTokens;
    } catch (error) {
      tokenLogger.error('tokens.get.failed', error);
      return null;
    }
  },

  /**
   * Clear all stored tokens.
   */
  clearTokens(): void {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('coi-live-token');
    tokenLogger.info('tokens.cleared');
  },

  /**
   * Check if tokens are expired or about to expire.
   */
  isExpired(): boolean {
    const tokens = this.getTokens();
    if (!tokens) {
      return true;
    }

    const now = Date.now();
    const isExpired = now >= tokens.expiresAt - TOKEN_REFRESH_BUFFER;

    if (isExpired) {
      tokenLogger.warn('tokens.expired', {
        expiresAt: new Date(tokens.expiresAt).toISOString(),
        now: new Date(now).toISOString(),
      });
    }

    return isExpired;
  },

  /**
   * Get time until token expiry in milliseconds.
   */
  getTimeUntilExpiry(): number | null {
    const tokens = this.getTokens();
    if (!tokens) {
      return null;
    }
    return Math.max(0, tokens.expiresAt - Date.now());
  },

  /**
   * Get the access token if valid.
   */
  getAccessToken(): string | null {
    if (this.isExpired()) {
      return null;
    }
    const tokens = this.getTokens();
    return tokens?.accessToken ?? null;
  },

  /**
   * Get the refresh token.
   */
  getRefreshToken(): string | null {
    const tokens = this.getTokens();
    return tokens?.refreshToken ?? null;
  },

  /**
   * Decode JWT payload (without verification).
   * Useful for extracting user info from ID token.
   */
  decodeToken(token: string): Record<string, unknown> | null {
    try {
      const [, payload] = token.split('.');
      if (!payload) {
        return null;
      }
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded) as Record<string, unknown>;
    } catch {
      tokenLogger.error('token.decode.failed', 'Invalid token format');
      return null;
    }
  },
};

export { tokenLogger };
